/**
 * Consolidated Ad Insertion System
 *
 * Generic ad insertion logic used by both homepage in-feed ads and trending widget ads.
 * This module provides a factory function to create specialized ad insertion systems
 * with configurable selectors, intervals, and slot naming.
 *
 * @module lib/adInsertion
 */

const { logWarn } = require('./logger');
const { ensureAdSlotMinHeight } = require('./adSizeUtils');
const { createAndStartObserver, scheduleInitialScans } = require('./utils/debouncedObserver');

/**
 * Ensure window.adData is initialized with required tracking properties
 * @param {object} [additionalProps] - Additional properties to initialize
 */
function ensureAdState(additionalProps = {}) {
  if (typeof window === 'undefined') return;
  if (!window.adData || typeof window.adData !== 'object') {
    window.adData = {};
  }

  const placedAdCount = Number(window.adData.placedAdCount);
  if (!Number.isFinite(placedAdCount) || placedAdCount < 1) {
    window.adData.placedAdCount = 1;
  } else {
    window.adData.placedAdCount = Math.floor(placedAdCount);
  }

  // Ensure additional props exist without discarding host-owned ad state.
  for (const key of Object.keys(additionalProps)) {
    if (typeof window.adData[key] !== 'number') {
      window.adData[key] = additionalProps[key];
    }
  }
}

/**
 * Create the standard ad HTML structure
 * @param {string} slotId - The ID for the ad slot div
 * @returns {string} HTML string for the ad wrapper
 */
function createAdHtml(slotId, minHeight = 0, wrapperClass = '') {
  const height = Number(minHeight);
  const inlineStyle = Number.isFinite(height) && height > 0
    ? ` style="min-height:${height}px;"`
    : '';
  const classes = ['ad-wrapper'];
  if (wrapperClass) {
    classes.push(wrapperClass);
  }
  return `<div class="${classes.join(' ')}"><div class="ad-flex"><div id="${slotId}" class="content-ad"${inlineStyle}></div></div></div>`;
}

/**
 * Insert an ad slot after a target element
 * @param {HTMLElement} targetElement - The element to insert after
 * @param {object} options - Insertion options
 * @param {string} options.slotPrefix - Prefix for slot ID
 * @param {string} options.markerClass - CSS class to add to inserted ad
 * @param {number} [options.minHeight] - Optional minimum height for the slot
 * @param {number} [options.maxAds] - Optional maximum number of ads (uses counter key)
 * @param {string} [options.counterKey] - Key in window.adData to track insertion count
 * @returns {boolean} Whether insertion was successful
 */
function insertAdAfterElement(targetElement, options) {
  const {
    slotPrefix,
    markerClass,
    minHeight = 0,
    maxAds,
    counterKey
  } = options;

  if (!targetElement || typeof targetElement.insertAdjacentHTML !== 'function') {
    return false;
  }

  ensureAdState(counterKey ? { [counterKey]: 0 } : {});

  // Check max ads limit if specified
  if (typeof maxAds === 'number' && counterKey && window.adData[counterKey] >= maxAds) {
    return false;
  }

  window.adData.placedAdCount += 1;
  const adNumber = window.adData.placedAdCount;
  const slotId = `${slotPrefix}-${adNumber}`;

  targetElement.insertAdjacentHTML('afterend', createAdHtml(slotId, minHeight));

  // Track counter if specified
  if (counterKey) {
    window.adData[counterKey] += 1;
  }

  // Add marker class to inserted element
  const inserted = targetElement.nextElementSibling;
  if (inserted && inserted.classList && markerClass) {
    inserted.classList.add(markerClass);
  }

  // Apply min height if specified
  if (minHeight > 0 && typeof document !== 'undefined') {
    ensureAdSlotMinHeight(document.getElementById(slotId), minHeight);
  }

  return true;
}

/**
 * Check if an element already has an ad after it
 * @param {HTMLElement} element - The element to check
 * @param {string} dataAttr - The data attribute to check
 * @param {string} markerClass - The marker class on ad wrappers
 * @returns {boolean} Whether element already has ad
 */
function hasAdAfter(element, dataAttr, markerClass) {
  if (element.getAttribute(dataAttr) === 'true') {
    return true;
  }

  const next = element.nextElementSibling;
  if (
    next &&
    next.classList &&
    (next.classList.contains('ad-wrapper') || next.classList.contains(markerClass))
  ) {
    return true;
  }

  return false;
}

/**
 * Create an ad insertion system with the given configuration
 * @param {object} config - System configuration
 * @param {string} config.name - System name for logging
 * @param {string|string[]} config.containerSelector - CSS selector(s) for containers
 * @param {string} config.itemClass - CSS class for items to insert ads after
 * @param {number} config.adInterval - Insert ad after every N items
 * @param {string} config.slotPrefix - Prefix for slot IDs
 * @param {string} config.markerClass - CSS class for inserted ads
 * @param {string} config.dataAttr - Data attribute to mark processed items
 * @param {string} config.scannedAttr - Data attribute to mark scanned containers
 * @param {number} [config.maxAds] - Maximum ads to insert (optional)
 * @param {string} [config.counterKey] - Key in window.adData for counter (optional)
 * @param {number} [config.minHeight] - Minimum slot height (optional)
 * @param {string[]} [config.skipClasses] - Classes that prevent insertion (e.g., slick-slider)
 * @param {function} [config.shouldScanNode] - Custom function to check if mutation should trigger scan
 * @returns {object} Ad insertion system with scan, insert, setupObserver methods
 */
function createAdInsertionSystem(config) {
  const {
    name,
    containerSelector,
    itemClass,
    adInterval,
    slotPrefix,
    markerClass,
    dataAttr,
    scannedAttr,
    maxAds,
    counterKey,
    minHeight = 0,
    skipClasses = [],
    shouldScanNode
  } = config;

  const selectors = Array.isArray(containerSelector) ? containerSelector : [containerSelector];

  /**
   * Insert ads into a single container
   * @param {HTMLElement} container - The container element
   * @param {object} [options] - Override options
   */
  function insertAdsInContainer(container, options = {}) {
    if (typeof document === 'undefined') return;
    if (!container || typeof container.querySelectorAll !== 'function') return;
    if (typeof window.insertNewAd !== 'function' && !options.forceInsert) return;

    const interval = typeof options.adInterval === 'number' ? options.adInterval : adInterval;
    const prefix = options.slotPrefix || slotPrefix;
    const marker = options.markerClass || markerClass;
    const attr = options.dataAttr || dataAttr;
    const max = typeof options.maxAds === 'number' ? options.maxAds : maxAds;
    const counter = options.counterKey || counterKey;
    const height = typeof options.minHeight === 'number' ? options.minHeight : minHeight;

    // Check skip classes (e.g., slick slider)
    if (container.classList) {
      for (const skipClass of skipClasses) {
        if (container.classList.contains(skipClass)) {
          return;
        }
      }
    }

    // Mark container as scanned
    if (scannedAttr) {
      container.setAttribute(scannedAttr, 'true');
    }

    const items = Array.from(container.querySelectorAll(`.${itemClass}`)).filter(
      (el) => el && el.nodeType === 1
    );
    if (items.length === 0) return;

    items.forEach((item, index) => {
      const shouldInsert = (index + 1) % interval === 0;
      if (!shouldInsert) return;

      // Check max ads limit
      if (typeof max === 'number' && counter) {
        ensureAdState({ [counter]: 0 });
        if (window.adData[counter] >= max) return;
      }

      // Check if already has ad
      if (hasAdAfter(item, attr, marker)) {
        item.setAttribute(attr, 'true');
        return;
      }

      try {
        // Use window.insertNewAd if available, otherwise use direct insertion
        if (typeof window.insertNewAd === 'function') {
          const beforeCount = window.adData && window.adData.placedAdCount;
          window.insertNewAd(item);
          const afterCount = window.adData && window.adData.placedAdCount;
          if (afterCount && beforeCount && afterCount === beforeCount) {
            return;
          }
          const inserted = item.nextElementSibling;
          if (inserted && inserted.classList) {
            inserted.classList.add(marker);
          }
          if (height > 0 && inserted) {
            const slot = inserted.querySelector
              ? inserted.querySelector('.content-ad')
              : null;
            if (slot) {
              ensureAdSlotMinHeight(slot, height);
            }
          }
        } else {
          insertAdAfterElement(item, {
            slotPrefix: prefix,
            markerClass: marker,
            minHeight: height,
            maxAds: max,
            counterKey: counter
          });
        }
        item.setAttribute(attr, 'true');
      } catch (err) {
        logWarn(`[${name}] Failed to insert ad`, err);
      }
    });
  }

  /**
   * Scan all matching containers and insert ads
   * @param {object} [options] - Override options
   */
  function scan(options = {}) {
    if (typeof document === 'undefined') return;

    const sels = options.selectors || selectors;
    const allContainers = new Set();

    for (const selector of sels) {
      const matches = document.querySelectorAll(selector);
      matches.forEach((el) => allContainers.add(el));
    }

    allContainers.forEach((container) => insertAdsInContainer(container, options));
  }

  /**
   * Set up MutationObserver to watch for new content
   * @param {object} [options] - Override options
   * @returns {MutationObserver|null}
   */
  function setupObserver(options = {}) {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return null;
    }

    const root = document.documentElement || document.body;
    if (!root) return null;

    // Create filter function for mutations
    const checkNode = shouldScanNode || ((node) => {
      if (!node || node.nodeType !== 1) return false;
      // Check if node matches container or item patterns
      if (node.classList && node.classList.contains(itemClass)) return true;
      if (node.querySelector && node.querySelector(`.${itemClass}`)) return true;
      for (const selector of selectors) {
        const baseClass = selector.split(' ').pop().replace('.', '');
        if (node.classList && node.classList.contains(baseClass)) return true;
        if (node.querySelector && node.querySelector(selector)) return true;
      }
      return false;
    });

    let queued = false;
    const queueScan = () => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        scan(options);
      }, 75);
    };

    const observer = new MutationObserver((mutations) => {
      let shouldScan = false;
      for (const mutation of mutations) {
        if (!mutation.addedNodes || mutation.addedNodes.length === 0) continue;
        for (const node of mutation.addedNodes) {
          if (checkNode(node)) {
            shouldScan = true;
            break;
          }
        }
        if (shouldScan) break;
      }
      if (shouldScan) {
        queueScan();
      }
    });

    try {
      observer.observe(root, { childList: true, subtree: true });
    } catch (err) {
      logWarn(`[${name}] Failed to start observer`, err);
      return null;
    }

    // Schedule initial scans
    scheduleInitialScans(() => scan(options));

    return observer;
  }

  return {
    insertAdsInContainer,
    scan,
    setupObserver,
    ensureAdState: () => ensureAdState(counterKey ? { [counterKey]: 0 } : {})
  };
}

/**
 * Create the insertNewAd function for use with ad insertion systems
 * @param {string} slotPrefix - Prefix for slot IDs
 * @param {number} [minHeight] - Optional minimum height for slots
 * @returns {function} The insertNewAd function
 */
function createInsertNewAdFunction(slotPrefix, minHeight = 0, options = {}) {
  const { wrapperClass = '', prepareSlot } = options;

  return function insertNewAd(node) {
    if (!node || !node.insertAdjacentHTML) return;
    ensureAdState();
    window.adData.placedAdCount += 1;
    const adNumber = window.adData.placedAdCount;
    const slotId = `${slotPrefix}-${adNumber}`;
    node.insertAdjacentHTML('afterend', createAdHtml(slotId, minHeight, wrapperClass));
    if (typeof document !== 'undefined') {
      const slot = document.getElementById(slotId);
      if (minHeight > 0) {
        ensureAdSlotMinHeight(slot, minHeight);
      }
      if (typeof prepareSlot === 'function') {
        prepareSlot(slot);
      }
    }
    return slotId;
  };
}

/**
 * Sync ad counter with existing DOM elements
 * @param {string} slotPrefix - Prefix for slot IDs to search for
 */
function syncPlacedAdCountFromDom(slotPrefix) {
  if (typeof document === 'undefined') return;
  ensureAdState();

  const regex = new RegExp(`^${slotPrefix}-(\\d+)$`);
  const matches = Array.from(document.querySelectorAll(`[id^="${slotPrefix}-"]`))
    .map((el) => (el && el.id ? el.id : ''))
    .map((id) => regex.exec(id))
    .filter(Boolean)
    .map((m) => Number(m[1]))
    .filter((n) => Number.isFinite(n));

  if (matches.length === 0) return;
  const max = Math.max.apply(null, matches);
  if (Number.isFinite(max) && max > window.adData.placedAdCount) {
    window.adData.placedAdCount = max;
  }
}

/**
 * Get slot prefix for a given brand and device
 * @param {string} brand - Brand name ('narcity' or 'mtlblog')
 * @param {string} device - Device type ('desktop' or 'mobile')
 * @param {string} [desktopPrefix='dt-content-ad'] - Desktop prefix
 * @returns {string} The appropriate slot prefix
 */
function getSlotPrefix(brand, device, desktopPrefix = 'dt-content-ad') {
  if (device === 'mobile') {
    return brand === 'mtlblog' ? 'bb-content-mtlblog' : 'bb-content-narcity';
  }
  return desktopPrefix;
}

module.exports = {
  ensureAdState,
  createAdHtml,
  insertAdAfterElement,
  hasAdAfter,
  createAdInsertionSystem,
  createInsertNewAdFunction,
  syncPlacedAdCountFromDom,
  getSlotPrefix
};
